/*
 *  Copyright IBM Corp. 2025, 2026
 *
 *  This source code is licensed under the Apache-2.0 license found in the
 *  LICENSE file in the root directory of this source tree.
 *
 *  @license
 */

import { ChatInstance } from "../instance/ChatInstance";
import { CustomSendMessageOptions } from "./MessagingConfig";
import { MessageRequest, StructuredData } from "../messaging/Messages";
import { CornersType, PerCornerConfig } from "./CornersType";
import { HomeScreenConfig } from "./HomeScreenConfig";
import type { LayoutCustomProperties } from "./LayoutCustomProperties";
import type {
  ServiceDesk,
  ServiceDeskFactoryParameters,
  ServiceDeskPublicConfig,
} from "./ServiceDeskConfig";
import { HistoryItem } from "../messaging/History";
import { LauncherConfig } from "./LauncherConfig";
import { DeepPartial } from "../utilities/DeepPartial";
import enLanguagePackData from "../../chat/languages/en.json";
import type { ToolbarAction } from "@carbon/ai-chat-components/es/react/toolbar.js";
import type { MarkdownItPlugin as _MarkdownItPlugin } from "@carbon/ai-chat-components/es/components/markdown/index.js";
import type { KeyboardShortcuts } from "./ShortcutConfig";

/**
 * This file contains the definition for the public application configuration operations that are provided by the
 * host page.
 */

/**
 * The raw strings used for {@link PublicConfig.strings}. Presented in ICU format.
 *
 * @category Config
 */
export const enLanguagePack = enLanguagePackData;

/**
 * A language pack represents the set of display strings for a particular language.
 * It defines all the text strings that can be customized for different languages.
 *
 * @category Config
 */
export type LanguagePack = typeof enLanguagePack;

/**
 * Configuration interface for Carbon AI Chat.
 *
 * @category Config
 */
export interface PublicConfig {
  /**
   * This is a one-off listener for catastrophic errors. This is used instead of a normal event bus handler because this function can be
   * defined and called before the event bus has been created.
   */
  onError?: (data: OnErrorData) => void;

  /**
   * By default, the chat window will be rendered in a "closed" state.
   */
  openChatByDefault?: boolean;

  /**
   * Disclaimer screen configuration.
   *
   * If `disclaimerHTML` changes after the disclaimer has been accepted, we request a user to accept again.
   */
  disclaimer?: DisclaimerPublicConfig;

  /**
   * This value is only used when a custom element is being used to render the widget. By default, a number of
   * enhancements to the widget are activated on mobile devices which can interfere with a custom element. This
   * value can be used to disable those enhancements while using a custom element.
   */
  disableCustomElementMobileEnhancements?: boolean;

  /**
   * Add a bunch of noisy console.log messages!
   */
  debug?: boolean;

  /**
   * Expose internal serviceManager on ChatInstance for testing purposes.
   * This should only be used in test environments.
   *
   * @internal
   */
  exposeServiceManagerForTesting?: boolean;

  /**
   * Which Carbon theme tokens to inject. If unset (falsy), the chat inherits tokens from the host page.
   * Set to a specific theme to force token injection.
   */
  injectCarbonTheme?: CarbonTheme;

  /**
   * Enables Carbon AI theme styling. Defaults to true.
   */
  aiEnabled?: boolean;

  /**
   * This is a factory for producing custom implementations of service desks. If this value is set, then this will
   * be used to create an instance of a {@link ServiceDesk} when the user attempts to connect to an agent.
   *
   * If it is changed in the middle of a conversation (you should obviously avoid this) the conversation with the
   * human agent will be disconnected.
   */
  serviceDeskFactory?: (
    parameters: ServiceDeskFactoryParameters,
  ) => Promise<ServiceDesk>;

  /**
   * Any public config to apply to service desks.
   */
  serviceDesk?: ServiceDeskPublicConfig;

  /**
   * If the Carbon AI Chat should grab focus if the chat is open on page load.
   */
  shouldTakeFocusIfOpensAutomatically?: boolean;

  /**
   * An optional namespace that can be added to the Carbon AI Chat that must be 30 characters or under. This value is
   * intended to enable multiple instances of the Carbon AI Chat to be used on the same page. The namespace for this web
   * chat. This value is used to generate a value to append to anything unique (id, session keys, etc) to allow
   * multiple Carbon AI Chats on the same page.
   *
   * Note: this value is used in the aria region label for the Carbon AI Chat. This means this value will be read out loud
   * by users using a screen reader.
   */
  namespace?: string;

  /**
   * Indicates if Carbon AI Chat should sanitize HTML from the assistant.
   */
  shouldSanitizeHTML?: boolean;

  /**
   * Extra config for controlling the behavior of the header.
   */
  header?: HeaderConfig;

  /**
   * The config object for chat history.
   */
  history?: HistoryConfig;

  /**
   * The config object for changing Carbon AI Chat's layout.
   */
  layout?: LayoutConfig;

  /**
   * Config options for controlling messaging.
   */
  messaging?: PublicConfigMessaging;

  /**
   * Sets the chat into a read only mode for displaying old conversations.
   */
  isReadonly?: boolean;

  /**
   * Allows for feedback to persist in all messages, not just the latest message.
   */
  persistFeedback?: boolean;

  /**
   * Sets the name of the assistant. Defaults to "watsonx". Used in screen reader announcements and error messages.
   */
  assistantName?: string;

  /**
   * Sets the URL pointing to a custom avatar for the response author. This image should be a square. If not provided, the default Watsonx icon will be used.
   */
  assistantAvatarUrl?: string;

  /**
   * The locale to use for the widget. This controls the language pack and regional formatting.
   * Example values include: 'en', 'en-us', 'fr', 'es'.
   */
  locale?: string;

  /**
   * Configuration for the homescreen.
   *
   * If you change anything but `is_on` after the chat session has started, the chat will handle it gracefully.
   *
   * If you turn on the homescreen after the user has already started chatting, it will show up in the header as
   * an icon, but the user won't be forced to go back to the homescreen (unlike turning on the disclaimer mid-chat).
   */
  homescreen?: HomeScreenConfig;

  /**
   * Configuration for the launcher.
   */
  launcher?: LauncherConfig;

  /**
   * Configuration for the main input field on the chat.
   */
  input?: InputConfig;

  /**
   * Configuration for file upload behavior in the chat input.
   * When `is_on` is `true`, the chat renders a file attachment button in the input area.
   *
   * @experimental
   */
  upload?: UploadConfig;

  /**
   * Optional partial language pack overrides. Values merge with defaults.
   */
  strings?: DeepPartial<LanguagePack>;

  /**
   * Configuration for keyboard shortcuts in the chat.
   * Allows customization of keyboard shortcuts for various actions.
   *
   * @experimental
   */
  keyboardShortcuts?: KeyboardShortcuts;

  /**
   * Markdown rendering customization. The framework-neutral subset; React and
   * web-component layers extend this with their own `customRenderers` member.
   *
   * @experimental
   */
  markdown?: PublicConfigMarkdown;
}

/**
 * Element type of {@link PublicConfigMarkdown.markdownItPlugins}. Either a
 * bare plugin function or a `[plugin, options]` / `[plugin, ...params]` tuple
 * matching `MarkdownIt.use(...)`.
 *
 * @category Config
 * @experimental
 */
export type MarkdownItPlugin = _MarkdownItPlugin;

/**
 * Framework-neutral markdown configuration shared by the React `ChatContainer`
 * and the `cds-aichat-container` web component. Each layer extends this with
 * its own `customRenderers` member returning the layer-appropriate type
 * (`ReactNode` vs `HTMLElement | null`).
 *
 * @category Config
 * @experimental
 */
export interface PublicConfigMarkdown {
  /**
   * Markdown-it plugins applied after the built-in plugins
   * (markdown-it-attrs, markdown-it-highlight, markdown-it-task-lists).
   * Memoize this array — a new reference each render rebuilds the
   * markdown-it instance.
   *
   * @experimental
   */
  markdownItPlugins?: MarkdownItPlugin[];
}

/**
 * A single menu option.
 *
 * @category Config
 */
export interface CustomMenuOption {
  /**
   * The text to display for the menu option.
   */
  text: string;

  /**
   * The callback handler to call when the option is selected.
   * Provide either this or `href`, but not both.
   */
  handler?: () => void;

  /**
   * The URL to navigate to when the option is selected.
   * Provide either this or `handler`, but not both.
   */
  href?: string;

  /**
   * The target attribute for the link when using `href`.
   * Defaults to "_self" if not specified.
   * Common values: "_self", "_blank", "_parent", "_top"
   */
  target?: string;

  /**
   * If true, the menu option will be disabled and cannot be selected.
   */
  disabled?: boolean;

  /**
   * Optional data-testid attribute for testing purposes.
   * This allows tests to reliably find and interact with specific menu options.
   */
  testId?: string;
}

/**
 * @category Config
 */
export enum MinimizeButtonIconType {
  /**
   * This shows an "X" icon.
   */
  CLOSE = "close",

  /**
   * This shows a "-" icon.
   */
  MINIMIZE = "minimize",

  /**
   * This shows an icon that indicates that the Carbon AI Chat can be collapsed into a side panel.
   */
  SIDE_PANEL_LEFT = "side-panel-left",

  /**
   * This shows an icon that indicates that the Carbon AI Chat can be collapsed into a side panel.
   */
  SIDE_PANEL_RIGHT = "side-panel-right",

  /**
   * This shows an icon that indicates that the Carbon AI Chat can be collapsed into a side panel.
   */
  SIDE_PANEL_DOWN = "side-panel-down",
}

/**
 * Configuration for the input field in the main chat and homescreen.
 *
 * @category Config
 */
export interface InputConfig {
  /**
   * The maximum number of characters allowed in the input field. Defaults to 10000.
   */
  maxInputCharacters?: number;

  /**
   * Controls whether the main input surface is visible when the chat loads.
   * Defaults to true.
   */
  isVisible?: boolean;

  /**
   * If true, the main input surface starts in a disabled (read-only) state.
   * Equivalent to {@link PublicConfig.isReadonly}, but scoped just to the assistant input.
   */
  isDisabled?: boolean;
}

/**
 * Configuration for file upload behavior in the chat input.
 *
 * @experimental
 * @category Config
 */
export interface UploadConfig {
  /**
   * Whether file upload is enabled. When `true`, the chat renders a file attachment button
   * in the input area. Defaults to `false`.
   *
   * If `is_on` is `true` but `onFileUpload` is not provided, an error is logged and
   * file upload is disabled.
   */
  is_on: boolean;

  /**
   * Accepted MIME types or file extensions, in the same format as the HTML `accept` attribute.
   * Examples: `"image/*"`, `".pdf,.docx"`, `"application/pdf"`.
   * If omitted, all file types are accepted.
   */
  accept?: string;

  /**
   * Maximum file size in bytes. Files exceeding this limit are rejected client-side
   * before `onFileUpload` is called.
   */
  maxFileSizeBytes?: number;

  /**
   * Maximum number of files that can be attached at once. If omitted, there is no limit.
   */
  maxFiles?: number;

  /**
   * Called once per file when the user selects it.
   *
   * Return a {@link StructuredData} object representing this file's contribution to the
   * pending message. The widget merges the returned `StructuredData` into
   * `pendingStructuredData` and tracks it per-upload so that individual files can be
   * removed before the message is sent.
   *
   * On failure: throw or return a rejected `Promise` — the widget marks the file as
   * errored in the UI.
   *
   * @param file - The `File` object selected by the user.
   * @param abortSignal - Fires if the user removes the pending upload before it
   *   completes, or if the chat is destroyed.
   *
   * @experimental
   */
  onFileUpload?: (
    file: File,
    abortSignal: AbortSignal,
  ) => Promise<StructuredData>;
}

/**
 * Configuration for the main header of the chat.
 *
 * @category Config
 */
export interface HeaderConfig {
  /**
   * If the chat should supply its own header. Can be false if you have a fullscreen chat or one embedded into a page and
   * you want to only make use of the main application header. Defaults to true.
   */
  isOn?: boolean;

  /**
   * Indicates the icon to use for the close button in the header.
   */
  minimizeButtonIconType?: MinimizeButtonIconType;

  /**
   * Hide the ability to minimize the Carbon AI Chat.
   */
  hideMinimizeButton?: boolean;

  /**
   * If true, shows the restart conversation button in the header of home screen and main chat.
   */
  showRestartButton?: boolean;

  /**
   * The chat header title.
   */
  title?: string;

  /**
   * The name displayed after the title.
   */
  name?: string;

  /**
   * All the currently configured custom menu options.
   */
  menuOptions?: CustomMenuOption[];

  /**
   * Controls whether to show the AI label/slug in the header. Defaults to true.
   *
   * There is currently no version of this that does not include the AI theme
   * blue gradients.
   */
  showAiLabel?: boolean;

  /**
   * Controls whether the default AI label content should be hidden.
   * The default content is only meant to serve as a placeholder and should be
   * replaced with custom content using:
   * {@link WriteableElementName.EXPLAINABILITY_POPOVER_CONTENT} and
   * {@link WriteableElementName.EXPLAINABILITY_POPOVER_ACTIONS}.
   * When set to true, all the default ai label content including the deprecated
   * {@link WriteableElementName.AI_TOOLTIP_AFTER_DESCRIPTION_ELEMENT}
   * writeable element will be removed.
   *
   * @default false
   */
  hideDefaultAiLabelContent?: boolean;

  /**
   * Controls whether the header should be constrained to the messages max width
   * (--cds-aichat-messages-max-width) or go full width. When true, the header
   * will be constrained to match the message width. When false (default), the
   * header will span the full width of the chat container.
   *
   * @default false
   */
  hasContentMaxWidth?: boolean;

  /**
   * Custom actions to display in the header toolbar. These actions can overflow
   * into a menu when space is limited.
   *
   * The icon property accepts CarbonIcon objects (from @carbon/web-components) or
   * React icon components (from @carbon/icons-react).
   *
   * Built-in buttons (restart, close) will be appended after these custom actions if
   * configured to be shown. You can, of course, disabled those OOTB icons and replace
   * them with your own.
   */
  actions?: ToolbarAction[];
}

/**
 * Configuration for the history panel of the chat.
 *
 * @category Config
 */
export interface HistoryConfig {
  /**
   * Indicates if the history panel should be shown.
   */
  isOn?: boolean;

  /**
   * Controls whether the mobile menu options (New chat, View chats) should be shown
   * in the header when the history panel is in mobile mode.
   *
   * When true (default), the mobile menu will appear in the header on small screens,
   * providing quick access to start a new chat or view chat history.
   *
   * When false, the mobile menu will be hidden even when in mobile mode.
   *
   * @default true
   */
  showMobileMenu?: boolean;

  /**
   * Controls whether history starts closed and enables state preservation across mode changes.
   *
   * When false (default):
   * - Desktop starts open, mobile starts closed
   * - Resizing between modes resets to default state
   *
   * When true:
   * - Both desktop and mobile start closed
   * - User's open/closed state is preserved when resizing between modes
   * - Enables external control via: instance.customPanels.getPanel(PanelType.HISTORY).open()/close()
   *
   * @default false
   */
  startClosed?: boolean;
}

/**
 * @category Config
 */
export interface LayoutConfig {
  /**
   * Indicates if the Carbon AI Chat widget should keep its border and box-shadow.
   */
  showFrame?: boolean;

  /**
   * Indicates if content inside the Carbon AI Chat widget should be constrained to a max-width.
   *
   * At larger widths the card, carousel, options and conversational search response types
   * have pending issues.
   */
  hasContentMaxWidth?: boolean;

  /**
   * Controls the corner style of the chat component.
   *
   * Can be a simple CornersType value to apply to all corners:
   * ```typescript
   * corners: CornersType.ROUND
   * ```
   *
   * Or a PerCornerConfig object to control each corner individually:
   * ```typescript
   * corners: {
   *   startStart: CornersType.ROUND,  // top-left in LTR
   *   startEnd: CornersType.ROUND,    // top-right in LTR
   *   endStart: CornersType.SQUARE,   // bottom-left in LTR
   *   endEnd: CornersType.SQUARE      // bottom-right in LTR
   * }
   * ```
   *
   * Undefined corners in PerCornerConfig will fall back to CornersType.ROUND.
   */
  corners?: CornersType | PerCornerConfig;

  /**
   * CSS variable overrides for the chat UI. This is a convienience method, you may also set these properties via CSS.
   *
   * Keys correspond to values from `LayoutCustomProperties` (e.g. `LayoutCustomProperties.height`),
   * which map to the underlying `--cds-aichat-…` custom properties.
   * Values are raw CSS values such as `"420px"`, `"9999"`, etc.
   *
   * Example:
   * { height: "560px", width: "420px" }
   */
  customProperties?: Partial<Record<LayoutCustomProperties, string>>;
}

/**
 * Config options for controlling messaging.
 *
 * @category Config
 */
export interface PublicConfigMessaging {
  /**
   * Indicates if Carbon AI Chat should make a request for the welcome message when a new conversation begins. If this is
   * true, then Carbon AI Chat will start with an empty conversation.
   *
   * **Manual session management required**: Changes to this property after conversation has started have no effect.
   * To apply new welcome behavior, call `instance.messaging.restartConversation()`.
   */
  skipWelcome?: boolean;

  /**
   * Changes the timeout used by the message service when making message calls. The timeout is in seconds. The
   * default is 150 seconds. After this time, an error will be shown in the client and an Abort signal will be sent
   * to customSendMessage. If set to 0, the chat will never timeout.  This is tied to either {@link ChatInstanceMessaging.addMessage} or
   * {@link ChatInstanceMessaging.addMessageChunk} being called after this message was sent. If neither of those methods
   * are called with in the window defined here, the chat will timeout (unless the value is set to 0).
   */
  messageTimeoutSecs?: number;

  /**
   * Controls how long AI chat should wait before showing the loading indicator. If set to 0, the chat will never show
   * the loading indicator on its own. This is tied to either {@link ChatInstanceMessaging.addMessage} or
   * {@link ChatInstanceMessaging.addMessageChunk} being called after this message was sent. If neither of those methods
   * are called with in the window defined here, the loading indicator will be shown.
   */
  messageLoadingIndicatorTimeoutSecs?: number;

  /**
   * A callback for Carbon AI Chat to use to send messages to your assistant.
   *
   * Carbon AI Chat will queue up any additional user messages until the Promise from a previous call to customSendMessage
   * has resolved. If you do not make customSendMessage async, it will be up to you to manage what happens when a message is
   * sent when the previous is still processing. If the Promise rejects, an error indicator will be displayed next to the user's message.
   *
   * If the request takes longer than PublicConfigMessaging.messageTimeoutSecs than the AbortSignal will be sent.
   */
  customSendMessage?: (
    request: MessageRequest,
    requestOptions: CustomSendMessageOptions,
    instance: ChatInstance,
  ) => Promise<void> | void;

  /**
   * This is a callback function that is used by Carbon AI Chat to retrieve history data for populating the Carbon AI Chat. If
   * this function is defined, it will be used instead of any other mechanism for fetching history.
   *
   * If this function is mutated after it was initially called, the chat does not re-call it.
   */
  customLoadHistory?: (instance: ChatInstance) => Promise<HistoryItem[]>;

  /**
   * Controls when the stop streaming button becomes visible during message streaming.
   *
   * You must have {@link PublicConfigMessaging.customSendMessage} return a promise for
   * this setting to work correctly.
   *
   * When `true`, the stop button appears immediately when `customSendMessage` is called,
   * allowing users to cancel requests before the first streaming chunk arrives. This is
   * useful for slow-starting requests or when you want to give users immediate control
   * over long-running operations. The button will remain visible as long as there is an
   * active streaming message, even if the initial message promise resolves.
   *
   * When `false` (default), the stop button only appears after the first streaming chunk
   * arrives with `cancellable: true` metadata, maintaining backward compatibility with
   * existing behavior.
   *
   */
  showStopButtonImmediately?: boolean;
}

/**
 * @category Config
 */
export interface DisclaimerPublicConfig {
  /**
   * If the disclaimer is turned on.
   */
  isOn: boolean;

  /**
   * HTML content to show in disclaimer.
   */
  disclaimerHTML: string;
}

/**
 * A string identifying what Carbon Theme we should base UI variables off of.
 * Defaults to "inherit". If you are not hosting the chat on a website that is Carbon styles, you will want to choose
 * once of the non-inherited values to inject the correct CSS custom property values into the code. See
 * https://carbondesignsystem.com/guidelines/color/tokens.
 *
 * @category Config
 */
export enum CarbonTheme {
  /**
   * Injects Carbon white theme tokens.
   */
  WHITE = "white",
  /**
   * Injects Carbon Gray 10 theme tokens.
   */
  G10 = "g10",
  /**
   * Injects Carbon Gray 90 theme tokens.
   */
  G90 = "g90",
  /**
   * Injects Carbon Gray 100 theme tokens.
   */
  G100 = "g100",
}

/**
 * The different categories of errors that the system can record. These values are published for end user consumption.
 *
 * @category Config
 */
export enum OnErrorType {
  /**
   * Indicates an error sending a message to the assistant. This error is only generated after all retries have
   * failed and the system has given up.
   */
  MESSAGE_COMMUNICATION = "MESSAGE_COMMUNICATION",

  /**
   * This indicates an error in one of the components that occurs as part of rendering the UI.
   */
  RENDER = "RENDER",

  /**
   * This indicates a known error with the configuration for a service desk. Fired when a connect_to_agent
   * response type is received, but none is configured.
   */
  INTEGRATION_ERROR = "INTEGRATION_ERROR",

  /**
   * This indicates that some error occurred while trying to hydrate the chat. This will prevent the chat from
   * functioning.
   */
  HYDRATION = "HYDRATION",
}

/**
 * Fired when a serious error in the chat occurs.
 *
 * @category Config
 */
export interface OnErrorData {
  /**
   * The type of error that occurred.
   */
  errorType: OnErrorType;

  /**
   * A message associated with the error.
   */
  message: string;

  /**
   * An extra blob of data associated with the error. This may be a stack trace for thrown errors.
   */
  otherData?: unknown;

  /**
   * If the error is of the severity that requires a whole restart of Carbon AI Chat.
   */
  catastrophicErrorType?: boolean;
}
